import { removeLogicFromText } from '@aytm/app/stats/page/components/lib/utils'
import { calcPlaceholderMultiSelect } from '@aytm/app/stats/page/question-views/components/lib/select-utils'

export const getOptionsList = ({ viewModeOptions, clusters, viewMode }) => {
  const clusterModes = ['heatmapclusters', 'overlayclusters']

  if (clusters.length > 0 || clusterModes.includes(viewMode)) {
    return viewModeOptions
  }

  return viewModeOptions.filter(el => !clusterModes.includes(el.value))
}

const removeTags = text => String(text ?? '').replace(/<[^>]*>/g, '')

// Which pin types are ticked. An empty list is what the researcher left when they turned every type
// off -- a filter they chose, not the absence of one -- so it stays empty rather than springing back
// to all. A config naming no list is unfiltered, and unfiltered means all of them; so is one whose
// every entry is unreadable, which is a bag that lost its contents rather than a choice.
export const getBatchesValue = ({ config, batches }) => {
  const stored = config?.batches

  if (Array.isArray(stored)) {
    const named = stored.filter(Boolean)

    if (named.length || !stored.length) return named.map(el => Number(el.split('b')?.[1]))
  }

  return Array.from({ length: batches.length }, (_, i) => i + 1)
}

export const getBatchesConfig = ({ value, batches, config }) => {
  const { batches: _, ...configWithoutBatches } = config

  const newConfig =
    value.length === batches.length ? configWithoutBatches : { ...config, batches: value.map(el => `b${el}`) }

  return newConfig
}

// View options stores the logic setting as `show` or `hide`; under `hide` the pin types read without their markers
export const getBatchesOptions = ({ batches, qLogicVisibility }) => {
  const qLogicHidden = 'hide' === qLogicVisibility

  return batches.map(batch => {
    const text = qLogicHidden ? removeLogicFromText(batch.text) : String(batch?.text)

    return {
      value: batch.position,
      text: removeTags(text)
    }
  })
}

export const getBatchesPlaceholder = (value, data) =>
  calcPlaceholderMultiSelect(value, data, {
    empty: 'No pin type selected',
    all: 'All pin types',
    multiple: count => `${count} pin type${count === 1 ? `` : `s`} selected:`
  })

export const getClustersPlaceholder = (value, data) =>
  calcPlaceholderMultiSelect(value, data, {
    empty: data?.length > 0 ? 'Hide all clusters' : 'No clusters found',
    all: 'Show all clusters',
    multiple: count => `Show selected cluster${count === 1 ? `` : `s`}:`
  })

export const calcSelectedClustersStat = (selectedClusters, answer) => {
  const points = answer?.heatmap?.points || []

  if (!selectedClusters.length || !points.length) {
    return { pins: 0, respondents: 0, comments: 0 }
  }

  const allPointIndexes = selectedClusters.flatMap(c => c.points || [])
  const uniquePointIndexes = [...new Set(allPointIndexes)]
  const pointsData = points.filter((_, i) => uniquePointIndexes.includes(i))

  const hasWeights = answer?.weights != null
  const usersUids = []
  let respondents = 0
  let comments = 0

  pointsData.forEach(point => {
    if (!usersUids.includes(point.uid)) {
      respondents += hasWeights ? Number(point.weights) : 1
      usersUids.push(point.uid)
    }
    if (point.comment) comments += 1
  })

  return {
    pins: allPointIndexes.length,
    respondents: hasWeights ? Number(respondents.toFixed(2)) : respondents,
    comments
  }
}
